import { styledWithInvalidProps } from "@components/Styled/StyledWithInvalidProps";
import { type Badge as BadgeProps } from "@ts/components";
import React, { forwardRef } from "react";
import tw from "twin.macro";
import { DesktopImages } from "./DesktopImages";
import { MobileImages } from "./MobileImages";
import { Thumbnails } from "./Thumbnails";
import { ImagesOutputProps, withImages } from "./withImages";

export type TouchDeviceProps = {
  isTouchDevice: boolean;
};

const Wrapper = styledWithInvalidProps("section")(
  ({ isTouchDevice }: TouchDeviceProps) => [
    tw`flex flex-col max-w-full lg:flex-row lg:flex-1 lg:pb-maxi`,
    isTouchDevice ? tw`lg:flex-col` : null,
  ],
);

const SlideContainer = tw.div`
  w-full lg:flex-1
`;

const ThumbnailContainer = styledWithInvalidProps("div")(
  ({ isTouchDevice }: TouchDeviceProps) => [
    tw`flex flex-row order-2 w-full lg:order-1 lg:flex-col lg:w-thumbnail lg:flex-none`,
    isTouchDevice ? tw`lg:order-2 lg:flex-row lg:w-full lg:flex-1` : null,
  ],
);

const Desktop = styledWithInvalidProps("div")(
  ({ isTouchDevice }: TouchDeviceProps) => [
    tw`hidden lg:block lg:w-full lg:order-2`,
    isTouchDevice ? tw`lg:hidden` : null,
  ],
);

const Mobile = styledWithInvalidProps("div")(
  ({ isTouchDevice }: TouchDeviceProps) => [
    tw`order-1 block lg:hidden`,
    isTouchDevice ? tw`lg:block` : null,
  ],
);

export const Badge = styledWithInvalidProps("span")(
  ({ colour, sale }: BadgeProps) => [
    tw`absolute left-mini top-mini px-micro py-pico text-caption z-20`,
    colour === `dark` ? tw`text-white bg-secondary` : tw`text-primary bg-white`,
    sale ? tw`px-micro` : null,
  ],
);

export const Images = withImages(
  forwardRef(
    (
      {
        badge,
        height,
        loaded,
        images,
        isTouchDevice,
        onImagesReady,
        onSlideChange,
        onSwiperInit,
        onThumbnailClick,
        onThumbsSwiperInit,
        onVideoPlay,
        playing,
        slideIndex,
        thumbsSwiper,
        video,
      }: ImagesOutputProps,
      containerRef,
    ) => (
      <Wrapper isTouchDevice={isTouchDevice}>
        <ThumbnailContainer isTouchDevice={isTouchDevice}>
          <Thumbnails
            height={height}
            images={images}
            isTouchDevice={isTouchDevice}
            loaded={loaded}
            onImagesReady={onImagesReady}
            onSwiperInit={onThumbsSwiperInit}
            onThumbnailClick={onThumbnailClick}
            slideIndex={slideIndex}
          />
        </ThumbnailContainer>
        <Mobile isTouchDevice={isTouchDevice}>
          <SlideContainer>
            <MobileImages
              badge={badge}
              images={images}
              onSlideChange={onSlideChange}
              onSwiperInit={onSwiperInit}
              onVideoPlay={onVideoPlay}
              playing={playing}
              thumbsSwiper={thumbsSwiper}
              video={video}
            />
          </SlideContainer>
        </Mobile>
        <Desktop isTouchDevice={isTouchDevice}>
          <DesktopImages
            badge={badge}
            height={height}
            images={images}
            ref={containerRef}
            slideIndex={slideIndex}
            video={video}
          />
        </Desktop>
      </Wrapper>
    ),
  ),
);
